import { logger } from '@fiverr-private/obs';
import { sendEvents } from '@fiverr-private/futile/lib/send/sendEvents';
import { safe } from '@fiverr-private/futile/lib/safe';
import { FILTER, SELECTORS } from '../config/filters';
import { removeEmptyEntries } from '../utils';
import { EVENTS_MAPPING, WIZARD_BQ_DATA } from '../utils/constants';
import localStorage from './localStorage';

const basePayload = ({ pageName, categoryName, subCategoryName, nestedSubCategoryName }) => ({
    'Page Name': pageName,
    'Category Name': categoryName,
    'Sub Category Name': subCategoryName,
    'Nested Sub Category Name': nestedSubCategoryName
});
const prepareMixpanelTrackPayload = ({ pageName, categoryName, subCategoryName, nestedSubCategoryName, payload = {} }) => removeEmptyEntries({
    ...basePayload({ pageName, categoryName, subCategoryName, nestedSubCategoryName }),
    ...payload
});
const prepareMixpanelViewPayload = ({ pageName, subCategoryName, categoryName, nestedSubCategoryName, page, sort, filters, query, numberOfResults, hasFiverrChoiceGigs, error }) => removeEmptyEntries({
    'Number Of Results': numberOfResults,
    'Query': query,
    'Sort': sort,
    'Applied Filters': filters,
    'Seen Fiverr Choice Badge': hasFiverrChoiceGigs,
    'Page Number': page,
    'Is Pro Experience': false,
    'V4 Perseus': true,
    'Error Type': error,
    ...basePayload({ pageName, categoryName, subCategoryName, nestedSubCategoryName })
});
const prepareMixpanelWizardPayload = ({ pageName, categoryName, subCategoryName, nestedSubCategoryName, payload = {} }) => removeEmptyEntries({
    'Wizard Type': 'sub_categories',
    'V4 Wizard': 'true',
    'Field Name': payload.name,
    'Field Value': _wizardTrackingFilterValue(payload.name, payload.value),
    'Step Number': payload.step,
    ...basePayload({ pageName, categoryName, subCategoryName, nestedSubCategoryName })
});
const prepareMixpanelFilterPayload = ({ pageName, categoryName, subCategoryName, nestedSubCategoryName, id, value, location, filters }) => removeEmptyEntries({
    ...basePayload({ pageName, categoryName, subCategoryName, nestedSubCategoryName }),
    'Filter Name': id,
    'Filter Value': value,
    'Filter Location': location,
    'Applied filters': filters
});
const prepareBiQueryFilterPayload = (reportData, type, id, value, location) => {

    const filter = {
        element: {
            metadata: id,
            text: value,
            position: location
        }
    };

    return removeEmptyEntries({
        type,
        group: 'listings_filters',
        listings: {
            ...reportData.listings,
            page: filter
        }
    });
};

const baseTrackResults = (newValue, oldValue) => ({
    value: newValue ? newValue : oldValue,
    type: _findFilterType(newValue)
});

export const filterTrackResults = {
    [SELECTORS.types.SINGLE]: baseTrackResults,
    [SELECTORS.types.PAIR]: baseTrackResults,
    [SELECTORS.types.MULTI]: (newValue = [], oldValue = []) => {
        const added = newValue.length > oldValue.length;

        // Concat values and get unique ( We actually want the delta between the two )
        const values = [...newValue, ...oldValue];

        return {
            value: values.find((v) => values.filter((val) => val === v).length === 1),
            type: _findFilterType(added)
        };
    }
};

class Tracker {

    initialize(context) {

        this.context = context;
    }

    track(type, payload, location) {
        const eventPayload = prepareMixpanelTrackPayload({ ...this.context, payload });
        const { reportData } = this.context;
        const { mixpanel, biquery } = type;
        const [id] = Object.keys(payload);


        mixpanel && MixpanelWrapper.trackEvent(
            mixpanel,
            eventPayload
        );

        biquery && sendEvents(prepareBiQueryFilterPayload(reportData, biquery, id, payload[id], location));

        return eventPayload;
    }

    trackFilter(type, { id, value, location } = {}) {

        const eventPayload = prepareMixpanelFilterPayload({ ...this.context, id, value, location });
        const { reportData } = this.context;
        const { mixpanel, biquery } = type;

        mixpanel && MixpanelWrapper.trackEvent(
            mixpanel,
            eventPayload
        );

        biquery && sendEvents(prepareBiQueryFilterPayload(reportData, biquery, id, value, location));

        return eventPayload;
    }

    // Update the bigQuery params
    filterTrackingUpdate = (currentFilter, filterChange, location, id) => {

        const filterType = SELECTORS.getType(id);
        const { type, value } = filterTrackResults[filterType](filterChange[id], currentFilter[id]);

        this.trackFilter(type, { id, value, location });
    }

    syncAndTrack({ filters, page, sort, reportData }, initialTime) {
        const load_time = new Date() - initialTime;
        const data = removeEmptyEntries({ ...reportData, page: { load_time } });

        this.context = {
            ...this.context,
            filters,
            page,
            sort,
            reportData
        };

        sendEvents(data);
    }

    view() {

        const eventPayload = prepareMixpanelViewPayload(this.context);
        const { reportData, localStorageData } = this.context;
        const { shouldStore, entrySuffix } = localStorageData;
        const loadTime = new Date() - new Date(reportData.page.v2_timestamp);

        MixpanelWrapper.trackEvent(
            EVENTS_MAPPING.PAGE_VIEWED.mixpanel,
            eventPayload
        );

        shouldStore && localStorage.storePageView(entrySuffix);

        const data = removeEmptyEntries({ ...reportData, page: { load_time: loadTime } });
        sendEvents(data);
    }

    click(target, additionalArgs = {}) {
        const payload = { ...additionalArgs, target };
        this.track(EVENTS_MAPPING.CLICK, payload);
    }

    error() {
        const eventPayload = prepareMixpanelViewPayload(this.context);

        MixpanelWrapper.trackEvent(
            EVENTS_MAPPING.PAGE_VIEWED.mixpanel,
            eventPayload
        );
    }

    trackHowFiverrWorksVideo(payload) {
        MixpanelWrapper.trackEvent(
            EVENTS_MAPPING.CLICK.TARGETS.HOW_FIVERR_WORKS_VIDEO,
            payload
        );
    }

    trackWizard(type, payload) {
        const eventPayload = prepareMixpanelWizardPayload({ ...this.context, payload });
        const { mixpanel, biquery } = type;

        mixpanel && MixpanelWrapper.trackEvent(
            mixpanel,
            eventPayload
        );

        if (biquery) {
            const { reportData, subCategoryName } = this.context;
            const { value: filterValue, name: filterName, step, options } = payload;
            const value = _wizardTrackingFilterValue(name, filterValue);

            // Click reported to BQ and GoogleTags as well
            sendEvents({
                ...WIZARD_BQ_DATA,
                wizard: {
                    answer_picked: value,
                    answer_options: options.join(','),
                    question_number:step,
                    question: filterName
                },
                listings: {
                    ...reportData.listings,
                    ...WIZARD_BQ_DATA.listings
                }
            });

            safe(Fiverr.pushGoogleGenericTag, null, {
                wizardClickedName: filterName,
                wizardClickedValue: value,
                subCategory: subCategoryName,
                nestedSubCategory: reportData.listings.nested_sub_category_id,
                event: 'wizardEvent'
            });
        }

        return eventPayload;
    }

    log(error, level = 'warn') {

        logger[level](error);
    }
}

const _wizardTrackingFilterValue = (id, value) => {

    if (id === FILTER.GIG_PRICE_RANGE.ID) {

        const rangeFilterValues = value.split(',');
        return `min price: ${rangeFilterValues[0]}, max price: ${rangeFilterValues[1]}`;
    }

    return value;
};

const _findFilterType = (value) => value ? EVENTS_MAPPING.FILTERS_APPLY : EVENTS_MAPPING.FILTER_REMOVE;

export default new Tracker();
